import {
  AdAccountView,
  CONNECTION_PATHS,
  failConnectionReconnectOperation,
  markConnectionAuthorized,
  startConnectionReconnectOperation,
  succeedConnectionReconnectOperation,
  useConnectionApiErrorHandler,
} from '@mint-lib/connections-ui';
import { useTranslation } from '@mint-lib/i18n';
import type { NormalizedConnection } from '@mint-lib/integration-registry';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import { Connection, OmniAuthBasic } from '@mint-sdk/api-omni';
import { useNavigate } from '@mint/routing';

/**
 * Ported from `packages/connections/src/components/_organisms/ManageReConnectionForm/useManageReConnectionHandlers.ts` —
 * the token/credentials (non-OAuth) reconnect flow for basic-credentials
 * platforms (currently zemanta). The connect (add-form) side lives in each
 * platform's own package (`@mint-lib/zemanta-integration`'s
 * `useZemantaConnect`) since connect mutations differ per platform;
 * `useBasicReconnect` stays here because the reconnect mutation is shared.
 */

export interface BasicConnectResult {
  connectionId: string | undefined;
  isPending: boolean;
}

export interface BasicReconnectResult {
  reconnect: (values: { username: string; password: string }) => void;
  isPending: boolean;
}

export interface BasicReconnectOptions {
  /** Form-local cleanup after a successful reconnect (reset + close the form). */
  onSuccess?: () => void;
}

/**
 * Shared by every basic-credentials platform — not a platform branch. On success, mirrors legacy's `useManageReConnectionHandlers`
 * + `ManageReConnectionForm`: success snackbar, refetch the connection list
 * and its verify query (so the expired alert and ad-accounts state update),
 * mark it authorized and switch to the available ad accounts tab. Failures
 * show an "Invalid Credentials" snackbar.
 */
export function useBasicReconnect(
  urn: string,
  connection: NormalizedConnection | undefined,
  { onSuccess }: BasicReconnectOptions = {},
): BasicReconnectResult {
  const { t } = useTranslation('@mint-lib/omni-integration');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate, isPending } = OmniAuthBasic.omniAuthBasicReconnect();
  const handleApiError = useConnectionApiErrorHandler({ flow: 'credentials' });

  const handleSuccess = (connectionId: string) => {
    succeedConnectionReconnectOperation();
    enqueueSnackbar({
      variant: 'success',
      subTitle: t('Your account has been successfully reconnected.'),
    });
    void Connection.getBffConnectionList.invalidateAll(queryClient, {});
    void Connection.verify.invalidateAll(queryClient, {
      urlParams: { connectionId },
    });
    markConnectionAuthorized(connectionId);
    onSuccess?.();
    void navigate({
      to: CONNECTION_PATHS.connectionsUpdate,
      params: { integration: urn, connection: connectionId },
      search: { tab: AdAccountView.available },
    });
  };

  return {
    reconnect: (values) => {
      if (!connection) return;
      startConnectionReconnectOperation({
        platform: urn,
        flow: 'basic',
        registry: true,
      });
      mutate(
        {
          data: {
            connectionId: connection.id,
            platform: urn,
            credentials: {
              username: values.username,
              password: values.password,
            },
          },
        },
        {
          onSuccess: () => handleSuccess(connection.id),
          onError: (error) => {
            failConnectionReconnectOperation('error');
            handleApiError(error);
          },
        },
      );
    },
    isPending,
  };
}
